import axiosInstance from '../../services/axiosInstance';
import { API_BASE_URL } from '../../config';
import { getAuthToken } from '../../services/authService';
import {
  KnowledgeHubAnswerActiveContext,
  KnowledgeHubAnswerActiveResultSet,
  KnowledgeHubAnswerHistoryItem,
  KnowledgeHubAnswerResponse,
  KnowledgeHubDocumentCoverage,
  KnowledgeHubHybridSearchResponse,
  KnowledgeHubIndexCoverage,
  KnowledgeHubIndexSummary,
  KnowledgeHubSearchResponse,
  KnowledgeHubSemanticSearchResponse,
  KnowledgeHubStatus,
  KnowledgeHubVectorIndexSummary,
  KnowledgeVisualEnrichmentJob,
  KnowledgeVisualEnrichmentPreflight,
  KnowledgeVisualProfileResponse,
  KnowledgeDocumentInspectorListItem,
  KnowledgeDocumentInspectorChunk,
  KnowledgePaginatedResponse,
  KnowledgeHubAgentResponse,
  KnowledgeHubAgentStreamEvent,
  KnowledgeHubConversationDetail,
  KnowledgeHubConversationSummary,
  KnowledgeHubAutoIndexJob,
  KnowledgeHubAutoIndexStatus,
} from './types';

export async function getKnowledgeHubStatus(): Promise<KnowledgeHubStatus> {
  const { data } = await axiosInstance.get<KnowledgeHubStatus>('/api/knowledge-hub/status');
  return data;
}

export async function runKnowledgeHubAssetIndex(limit: number): Promise<KnowledgeHubIndexSummary> {
  const { data } = await axiosInstance.post<KnowledgeHubIndexSummary>('/api/knowledge-hub/index/assets', {}, {
    params: { limit },
  });
  return data;
}

export async function runKnowledgeHubAssetIndexBatch(limit: number, offset = 0): Promise<KnowledgeHubIndexSummary> {
  const { data } = await axiosInstance.post<KnowledgeHubIndexSummary>('/api/knowledge-hub/index/assets', {
    limit,
    offset,
  });
  return data;
}

export async function getKnowledgeHubIndexCoverage(): Promise<KnowledgeHubIndexCoverage> {
  const { data } = await axiosInstance.get<KnowledgeHubIndexCoverage>('/api/knowledge-hub/index/coverage');
  if (!data?.assets || !data?.chunks) {
    throw new Error('Knowledge Hub returnerade ofullständig indexstatus. Försök uppdatera täckningen igen.');
  }
  return data;
}

export async function getKnowledgeHubDocumentCoverage(): Promise<KnowledgeHubDocumentCoverage> {
  const { data } = await axiosInstance.get<KnowledgeHubDocumentCoverage>('/api/knowledge-hub/documents/coverage');
  return data;
}

export async function getKnowledgeHubAutoIndexStatus(): Promise<KnowledgeHubAutoIndexStatus> {
  const { data } = await axiosInstance.get<KnowledgeHubAutoIndexStatus>('/api/knowledge-hub/autoindex/status');
  return data;
}

export async function listKnowledgeHubAutoIndexJobs(params: {
  status?: string;
  sourceType?: string;
  limit?: number;
} = {}): Promise<KnowledgeHubAutoIndexJob[]> {
  const { data } = await axiosInstance.get<KnowledgeHubAutoIndexJob[]>('/api/knowledge-hub/autoindex/jobs', { params });
  return Array.isArray(data) ? data : [];
}

export async function retryKnowledgeHubAutoIndexJob(jobId: string): Promise<KnowledgeHubAutoIndexJob> {
  const { data } = await axiosInstance.post<KnowledgeHubAutoIndexJob>(
    `/api/knowledge-hub/autoindex/jobs/${encodeURIComponent(jobId)}/retry`,
  );
  return data;
}

export async function cancelKnowledgeHubAutoIndexJob(jobId: string): Promise<KnowledgeHubAutoIndexJob> {
  const { data } = await axiosInstance.post<KnowledgeHubAutoIndexJob>(
    `/api/knowledge-hub/autoindex/jobs/${encodeURIComponent(jobId)}/cancel`,
  );
  return data;
}

export async function runKnowledgeHubAutoIndexOnce(): Promise<Record<string, unknown>> {
  const { data } = await axiosInstance.post<Record<string, unknown>>('/api/knowledge-hub/autoindex/run-once');
  return data;
}

export async function runKnowledgeHubDocumentIndex(
  limit: number,
  force = false,
  offset = 0,
  reconcile = false,
  extractionStatuses: string[] = [],
): Promise<KnowledgeHubIndexSummary> {
  const { data } = await axiosInstance.post<KnowledgeHubIndexSummary>('/api/knowledge-hub/index/documents', {
    limit,
    force,
    offset,
    reconcile,
    extractionStatuses,
  });
  return data;
}

export async function preflightKnowledgeVisualEnrichment(assetIds: string[]): Promise<KnowledgeVisualEnrichmentPreflight> {
  const { data } = await axiosInstance.post<KnowledgeVisualEnrichmentPreflight>(
    '/api/knowledge-hub/visual-enrichment/preflight',
    { assetIds },
  );
  return data;
}

export async function startKnowledgeVisualEnrichmentJob(
  assetIds: string[],
  force = false,
): Promise<KnowledgeVisualEnrichmentJob> {
  const { data } = await axiosInstance.post<KnowledgeVisualEnrichmentJob>(
    '/api/knowledge-hub/visual-enrichment/jobs',
    { assetIds, force },
  );
  return data;
}

export async function getKnowledgeVisualEnrichmentJob(jobId: string): Promise<KnowledgeVisualEnrichmentJob> {
  const { data } = await axiosInstance.get<KnowledgeVisualEnrichmentJob>(
    `/api/knowledge-hub/visual-enrichment/jobs/${encodeURIComponent(jobId)}`,
  );
  return data;
}

export async function cancelKnowledgeVisualEnrichmentJob(jobId: string): Promise<KnowledgeVisualEnrichmentJob> {
  const { data } = await axiosInstance.post<KnowledgeVisualEnrichmentJob>(
    `/api/knowledge-hub/visual-enrichment/jobs/${encodeURIComponent(jobId)}/cancel`,
  );
  return data;
}

export async function retryKnowledgeVisualEnrichmentJob(jobId: string): Promise<KnowledgeVisualEnrichmentJob> {
  const { data } = await axiosInstance.post<KnowledgeVisualEnrichmentJob>(
    `/api/knowledge-hub/visual-enrichment/jobs/${encodeURIComponent(jobId)}/retry`,
  );
  return data;
}

export async function getKnowledgeVisualProfile(assetId: string): Promise<KnowledgeVisualProfileResponse> {
  const { data } = await axiosInstance.get<KnowledgeVisualProfileResponse>(
    `/api/knowledge-hub/assets/${encodeURIComponent(assetId)}/visual-profile`,
  );
  return data;
}

function normalizePaginatedResponse<T>(data: any): KnowledgePaginatedResponse<T> {
  return {
    page: Number(data?.page || 1),
    limit: Number(data?.limit || 20),
    total: Number(data?.total || 0),
    pages: Number(data?.pages || 0),
    items: Array.isArray(data?.items) ? data.items : [],
  };
}

export async function listKnowledgeDocumentInspectorDocuments(params: {
  page?: number;
  limit?: number;
  query?: string;
  sourceKind?: string;
  documentType?: string;
  extractionStatus?: string;
  extractionQuality?: string;
} = {}): Promise<KnowledgePaginatedResponse<KnowledgeDocumentInspectorListItem>> {
  const { data } = await axiosInstance.get<KnowledgePaginatedResponse<KnowledgeDocumentInspectorListItem>>(
    '/api/knowledge-hub/document-inspector/documents',
    { params },
  );
  return normalizePaginatedResponse<KnowledgeDocumentInspectorListItem>(data);
}

export async function getKnowledgeDocumentInspectorHeader(entityId: string): Promise<Record<string, any>> {
  const { data } = await axiosInstance.get<Record<string, any>>(
    `/api/knowledge-hub/document-inspector/documents/${encodeURIComponent(entityId)}`,
  );
  return data;
}

export async function listKnowledgeDocumentInspectorChunks(
  entityId: string,
  params: Record<string, unknown> = {},
): Promise<KnowledgePaginatedResponse<KnowledgeDocumentInspectorChunk>> {
  const { data } = await axiosInstance.get<KnowledgePaginatedResponse<KnowledgeDocumentInspectorChunk>>(
    `/api/knowledge-hub/document-inspector/documents/${encodeURIComponent(entityId)}/chunks`,
    { params },
  );
  return normalizePaginatedResponse<KnowledgeDocumentInspectorChunk>(data);
}

export async function getKnowledgeDocumentInspectorChunkText(
  entityId: string,
  chunkId: string,
): Promise<Record<string, any>> {
  const { data } = await axiosInstance.get<Record<string, any>>(
    `/api/knowledge-hub/document-inspector/documents/${encodeURIComponent(entityId)}/chunks/${encodeURIComponent(chunkId)}`,
  );
  return data;
}

export async function getKnowledgeDocumentInspectorProfile(entityId: string): Promise<Record<string, any>> {
  const { data } = await axiosInstance.get<Record<string, any>>(
    `/api/knowledge-hub/document-inspector/documents/${encodeURIComponent(entityId)}/profile`,
  );
  return data;
}

export async function runKnowledgeHubProfileIndex(limit: number, force = false): Promise<KnowledgeHubIndexSummary> {
  const { data } = await axiosInstance.post<KnowledgeHubIndexSummary>('/api/knowledge-hub/index/profiles', {
    limit,
    force,
  });
  return data;
}

export async function searchKnowledgeHubEntities(query: string, limit = 10): Promise<KnowledgeHubSearchResponse> {
  const { data } = await axiosInstance.get<KnowledgeHubSearchResponse>('/api/knowledge-hub/entities/search', {
    params: { q: query, limit },
  });
  return data;
}

export async function lookupKnowledgeHubArticle(articleNumber: string, limit = 10): Promise<KnowledgeHubSearchResponse> {
  const { data } = await axiosInstance.get<KnowledgeHubSearchResponse>(
    `/api/knowledge-hub/article/${encodeURIComponent(articleNumber)}`,
    { params: { limit } },
  );
  return data;
}

export async function runKnowledgeHubVectorIndex(limit: number, force = false): Promise<KnowledgeHubVectorIndexSummary> {
  const { data } = await axiosInstance.post<KnowledgeHubVectorIndexSummary>('/api/knowledge-hub/index/vectors', {
    limit,
    force,
  });
  return data;
}

export async function searchKnowledgeHubSemantic(query: string, limit = 10): Promise<KnowledgeHubSemanticSearchResponse> {
  const { data } = await axiosInstance.post<KnowledgeHubSemanticSearchResponse>('/api/knowledge-hub/search/semantic', {
    query,
    limit,
  });
  return data;
}

export async function searchKnowledgeHubHybrid(query: string, limit = 10): Promise<KnowledgeHubHybridSearchResponse> {
  const { data } = await axiosInstance.post<KnowledgeHubHybridSearchResponse>('/api/knowledge-hub/search/hybrid', {
    query,
    limit,
  });
  return data;
}

export async function answerKnowledgeHubQuestion(
  query: string,
  limit = 8,
  history: KnowledgeHubAnswerHistoryItem[] = [],
  activeContext?: KnowledgeHubAnswerActiveContext | null,
  activeResultSet?: KnowledgeHubAnswerActiveResultSet | null,
): Promise<KnowledgeHubAnswerResponse> {
  const { data } = await axiosInstance.post<KnowledgeHubAnswerResponse>('/api/knowledge-hub/answer', {
    query,
    limit,
    history,
    activeContext,
    activeResultSet,
  });
  return data;
}

export async function getLatestKnowledgeHubConversation(): Promise<KnowledgeHubConversationDetail | null> {
  try {
    const { data } = await axiosInstance.get<KnowledgeHubConversationDetail>('/api/knowledge-hub/conversations/latest');
    return data;
  } catch (error: any) {
    if (error?.response?.status === 404) return null;
    throw error;
  }
}

export async function listKnowledgeHubConversations(limit = 20): Promise<KnowledgeHubConversationSummary[]> {
  const { data } = await axiosInstance.get<{ conversations: KnowledgeHubConversationSummary[] }>(
    '/api/knowledge-hub/conversations',
    { params: { limit } },
  );
  return data.conversations || [];
}

export async function getKnowledgeHubConversation(conversationId: string): Promise<KnowledgeHubConversationDetail | null> {
  try {
    const { data } = await axiosInstance.get<KnowledgeHubConversationDetail>(
      `/api/knowledge-hub/conversations/${encodeURIComponent(conversationId)}`,
    );
    return data;
  } catch (error: any) {
    if (error?.response?.status === 404) return null;
    throw error;
  }
}

export interface KnowledgeHubAgentNdjsonParseError {
  line: string;
  message: string;
}

export function parseKnowledgeAgentNdjsonChunk(
  buffered: string,
  nextChunk: string,
): {
  events: KnowledgeHubAgentStreamEvent[];
  errors: KnowledgeHubAgentNdjsonParseError[];
  remaining: string;
} {
  const combined = `${buffered}${nextChunk}`;
  const lines = combined.split(/\r?\n/);
  const remaining = lines.pop() || '';
  const events: KnowledgeHubAgentStreamEvent[] = [];
  const errors: KnowledgeHubAgentNdjsonParseError[] = [];
  lines.forEach((rawLine) => {
    const line = rawLine.trim();
    if (!line) return;
    try {
      events.push(JSON.parse(line) as KnowledgeHubAgentStreamEvent);
    } catch (error) {
      errors.push({
        line,
        message: error instanceof Error ? error.message : 'Ogiltig NDJSON-rad.',
      });
    }
  });
  return { events, errors, remaining };
}

export async function runKnowledgeHubAgent(
  input: {
    query: string;
    history?: KnowledgeHubAnswerHistoryItem[];
    activeContext?: KnowledgeHubAnswerActiveContext | null;
    activeResultSet?: KnowledgeHubAnswerActiveResultSet | null;
    conversationId?: string;
    contextState?: KnowledgeHubAgentResponse['contextState'];
    previousSources?: KnowledgeHubAgentResponse['sources'];
    depth?: 'standard' | 'deep';
  },
  onEvent: (event: KnowledgeHubAgentStreamEvent) => void | Promise<void>,
  signal?: AbortSignal,
): Promise<KnowledgeHubAgentResponse> {
  const token = getAuthToken();
  const response = await fetch(`${API_BASE_URL}/api/knowledge-hub/agent/run`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(input),
    signal,
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    const error = new Error(payload?.message || `Athena Agent misslyckades (${response.status}).`);
    (error as any).status = response.status;
    (error as any).code = payload?.code;
    throw error;
  }
  if (!response.body) throw new Error('Athena Agent returnerade ingen dataström.');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffered = '';
  let completed: KnowledgeHubAgentResponse | null = null;
  const parseErrors: KnowledgeHubAgentNdjsonParseError[] = [];
  while (true) {
    const { value, done } = await reader.read();
    const parsed = parseKnowledgeAgentNdjsonChunk(buffered, decoder.decode(value || new Uint8Array(), { stream: !done }));
    buffered = parsed.remaining;
    parseErrors.push(...parsed.errors);
    for (const event of parsed.events) {
      await onEvent(event);
      if (event.type === 'run_completed') completed = event.response;
      if (event.type === 'run_failed') throw new Error(event.error);
    }
    if (done) break;
  }
  const finalLine = buffered.trim();
  if (finalLine) {
    const parsed = parseKnowledgeAgentNdjsonChunk('', `${finalLine}\n`);
    parseErrors.push(...parsed.errors);
    for (const event of parsed.events) {
      await onEvent(event);
      if (event.type === 'run_completed') completed = event.response;
      if (event.type === 'run_failed') throw new Error(event.error);
    }
  }
  if (!completed) {
    const malformedSuffix = parseErrors.length > 0
      ? ` ${parseErrors.length} ogiltig${parseErrors.length === 1 ? '' : 'a'} dataströmsrad${parseErrors.length === 1 ? '' : 'er'} ignorerades.`
      : '';
    throw new Error(`Athena Agent avslutades utan ett färdigt svar.${malformedSuffix}`);
  }
  return completed;
}

export async function sendKnowledgeHubAgentFeedback(
  runId: string,
  rating: 'positive' | 'negative',
  note?: string,
): Promise<void> {
  await axiosInstance.post(
    `/api/knowledge-hub/agent/runs/${encodeURIComponent(runId)}/feedback`,
    { rating, note },
  );
}
